import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Buildings, ChatDots, PlusLg, SendFill, Headset } from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import {
  BRAND_BUILDING_TOPIC,
  createSupportThread,
  fetchSupportMessages,
  fetchSupportThreads,
  markSupportRead,
  sendSupportMessage,
} from '../api/brandBuildings';
import { SUPPORT_PATH } from '../components/building/BrandBuildingSection';
import './SupportPage.scss';

// Partner ↔ Outfino support chat (/dashboard/support[/:threadId|/new]), on
// the API's existing support tickets (the same chat the mobile app and the
// admin Support page use). Every ticket of the selected brand is listed, so
// the whole team sees them. The API also has a socket.io `/support`
// namespace, but the portal ships no socket client — it polls instead: the
// open conversation every few seconds, the list less often, and neither while
// the tab is hidden.

const MESSAGE_POLL_MS = 5000;
const THREAD_POLL_MS = 20000;
const BODY_MAX = 4000;

function usePolling(fn, ms, deps) {
  useEffect(() => {
    let timer = null;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      if (!document.hidden) await fn().catch(() => {});
      if (!stopped) timer = setTimeout(tick, ms);
    };
    tick();
    const onVis = () => {
      if (!document.hidden) {
        clearTimeout(timer);
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

function fmtTime(iso, lang) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const loc = lang === 'hu' ? 'hu-HU' : 'en-GB';
  return sameDay
    ? d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString(loc, { month: 'short', day: 'numeric' });
}

function fmtFull(iso, lang) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(lang === 'hu' ? 'hu-HU' : 'en-GB', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function topicLabel(t, topic) {
  return topic === BRAND_BUILDING_TOPIC ? t('support.topic.brand_building') : t('support.topic.general');
}

export default function SupportPage({ brand, threadId }) {
  const { t } = useLocale();
  const [threads, setThreads] = useState(null); // null = loading
  const [listError, setListError] = useState(false);

  const loadThreads = useCallback(async () => {
    try {
      const list = await fetchSupportThreads(brand.id);
      list.sort((a, b) => String(b.lastMessageAt || '').localeCompare(String(a.lastMessageAt || '')));
      setThreads(list);
      setListError(false);
    } catch {
      setListError(true);
      setThreads((prev) => prev || []);
    }
  }, [brand.id]);

  usePolling(loadThreads, THREAD_POLL_MS, [loadThreads]);

  const isNew = threadId === 'new';
  const showPane = !!threadId;

  return (
    <div className={`support${showPane ? ' has-pane' : ''}`}>
      <div className="support__head">
        <div>
          <h1 className="support__title">{t('support.title')}</h1>
          <p className="support__lead">{t('support.lead')}</p>
        </div>
        <Link className="btn-o btn-o--dark btn-o--sm" to={`${SUPPORT_PATH}/new`}>
          <PlusLg size={14} /> {t('support.new')}
        </Link>
      </div>

      <div className={`support__layout panel-card${showPane ? ' has-pane' : ''}`}>
        <aside className="support__list" aria-label={t('support.listLabel')}>
          {threads === null ? (
            <div className="support__muted">{t('shell.loading')}</div>
          ) : threads.length === 0 ? (
            <div className="support__empty-list">
              <ChatDots size={22} />
              <span>{listError ? t('support.listError') : t('support.noThreads')}</span>
            </div>
          ) : (
            <ul>
              {threads.map((th) => (
                <li key={th.id}>
                  <Link
                    to={`${SUPPORT_PATH}/${th.id}`}
                    className={`support-thread${th.id === threadId ? ' is-active' : ''}`}
                    aria-current={th.id === threadId ? 'page' : undefined}
                  >
                    <span className={`support-thread__icon${th.topic === BRAND_BUILDING_TOPIC ? ' is-building' : ''}`}>
                      {th.topic === BRAND_BUILDING_TOPIC ? <Buildings size={15} /> : <ChatDots size={15} />}
                    </span>
                    <span className="support-thread__main">
                      <span className="support-thread__row">
                        <strong>{topicLabel(t, th.topic)}</strong>
                        <ThreadTime iso={th.lastMessageAt} />
                      </span>
                      <span className="support-thread__row">
                        <span className="support-thread__preview">{th.lastMessage || th.preview || '—'}</span>
                        {th.status === 'closed' ? (
                          <span className="support-thread__status">{t('support.closed')}</span>
                        ) : th.unread > 0 ? (
                          <span className="support-thread__unread">{th.unread}</span>
                        ) : null}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <div className="support__pane">
          {isNew ? (
            <NewThread brand={brand} onCreated={loadThreads} />
          ) : threadId ? (
            <ChatView
              key={threadId}
              threadId={threadId}
              summary={threads?.find((x) => x.id === threadId) || null}
              onActivity={loadThreads}
            />
          ) : (
            <div className="support__placeholder">
              <span className="support__placeholder-icon"><Headset size={26} /></span>
              <strong>{t('support.pickTitle')}</strong>
              <span>{t('support.pickBody')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ThreadTime({ iso }) {
  const { lang } = useLocale();
  return <time className="support-thread__time" dateTime={iso || undefined}>{fmtTime(iso, lang)}</time>;
}

function BackToList() {
  const { t } = useLocale();
  return (
    <Link className="support-chat__back" to={SUPPORT_PATH}>
      <ArrowLeft size={14} /> {t('support.back')}
    </Link>
  );
}

function NewThread({ brand, onCreated }) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!body.trim() || busy) return;
    setBusy(true);
    setError(false);
    try {
      const th = await createSupportThread(brand.id, { message: body.trim() });
      await onCreated?.();
      navigate(th?.id ? `${SUPPORT_PATH}/${th.id}` : SUPPORT_PATH, { replace: true, state: { justCreated: true } });
    } catch {
      setError(true);
      setBusy(false);
    }
  }

  return (
    <form className="support-new" onSubmit={submit}>
      <BackToList />
      <h2 className="support-chat__title">{t('support.newTitle')}</h2>
      <p className="support__lead">{t('support.newLead')}</p>
      <label className="field">
        <span>{t('support.newLabel')}</span>
        <textarea rows={6} value={body} maxLength={BODY_MAX} autoFocus onChange={(e) => setBody(e.target.value)} placeholder={t('support.newHint')} />
      </label>
      {error && <div className="bbp-form__error">{t('support.sendError')}</div>}
      <div>
        <button type="submit" className="btn-o btn-o--dark" disabled={!body.trim() || busy}>
          {busy ? t('support.sending') : t('support.start')}
        </button>
      </div>
    </form>
  );
}

function ChatView({ threadId, summary, onActivity }) {
  const { t, lang } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState(null);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState([]); // optimistic sends: { tempId, body, failed }
  const listRef = useRef(null);
  const stickRef = useRef(true);
  const readRef = useRef(null);
  const justCreated = !!location.state?.justCreated;
  const alreadyOpen = !!location.state?.alreadyOpen;

  const load = useCallback(async () => {
    try {
      const list = await fetchSupportMessages(threadId);
      setMessages(list);
      setError(null);
      // Mark read when something new from Outfino has arrived.
      const last = list[list.length - 1];
      if (last && last.senderType === 'outfino' && readRef.current !== last.id) {
        readRef.current = last.id;
        markSupportRead(threadId).then(() => onActivity?.()).catch(() => {});
      }
    } catch (e) {
      setError(e?.status === 404 || e?.status === 403 ? 'notFound' : 'load');
      setMessages((prev) => prev || []);
    }
  }, [threadId, onActivity]);

  usePolling(load, MESSAGE_POLL_MS, [load]);

  // Keep the newest message in view while the reader is at the bottom.
  const onScroll = () => {
    const el = listRef.current;
    if (el) stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [messages, pending]);

  async function send(text, tempId) {
    const id = tempId || `tmp-${Date.now()}`;
    setPending((p) => (tempId ? p.map((x) => (x.tempId === id ? { ...x, failed: false } : x)) : [...p, { tempId: id, body: text, failed: false }]));
    stickRef.current = true;
    try {
      const { newTicket } = await sendSupportMessage(threadId, text);
      if (newTicket?.id && newTicket.id !== threadId) {
        // Writing into a closed ticket opened a new one — follow it.
        await onActivity?.();
        navigate(`${SUPPORT_PATH}/${newTicket.id}`, { replace: true });
        return;
      }
      await load();
      setPending((p) => p.filter((x) => x.tempId !== id));
      onActivity?.();
    } catch {
      setPending((p) => p.map((x) => (x.tempId === id ? { ...x, failed: true } : x)));
    }
  }

  function submit(e) {
    e?.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    send(text);
  }

  const meta = summary;
  const closed = meta?.status === 'closed';

  return (
    <div className="support-chat">
      <div className="support-chat__head">
        <BackToList />
        <div className="support-chat__headline">
          <h2 className="support-chat__title">
            {meta ? topicLabel(t, meta.topic) : t('support.conversation')}
          </h2>
          {meta?.createdAt && <span className="support-chat__sub">{t('support.started', { date: fmtFull(meta.createdAt, lang) })}</span>}
        </div>
        {closed && <span className="support-thread__status">{t('support.closed')}</span>}
      </div>

      <div className="support-chat__list" ref={listRef} onScroll={onScroll} aria-live="polite">
        {(justCreated || alreadyOpen) && (
          <div className="support-chat__notice">{t(justCreated ? 'support.createdNotice' : 'support.alreadyOpenNotice')}</div>
        )}
        {messages === null ? (
          <div className="support__muted">{t('shell.loading')}</div>
        ) : error === 'notFound' ? (
          <div className="support__muted">{t('support.notFound')}</div>
        ) : (
          <>
            {messages.length === 0 && !pending.length && <div className="support__muted">{t('support.noMessages')}</div>}
            {messages.map((m) => (
              <Bubble
                key={m.id}
                mine={m.senderType !== 'outfino'}
                body={m.body}
                imageUrl={m.imageUrl}
                time={fmtFull(m.createdAt, lang)}
                author={m.senderType === 'outfino' ? (m.senderName ? `${m.senderName} · Outfino` : t('support.outfinoTeam')) : null}
              />
            ))}
            {pending.map((p) => (
              <Bubble
                key={p.tempId}
                mine
                body={p.body}
                time={p.failed ? null : t('support.sending')}
                failed={p.failed}
                onRetry={() => send(p.body, p.tempId)}
              />
            ))}
          </>
        )}
        {error === 'load' && messages?.length > 0 && <div className="support__muted">{t('support.loadError')}</div>}
      </div>

      {error !== 'notFound' && (
        <form className="support-chat__composer" onSubmit={submit}>
          <textarea
            rows={1}
            value={draft}
            maxLength={BODY_MAX}
            placeholder={closed ? t('support.closedHint') : t('support.composerHint')}
            aria-label={t('support.composerHint')}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <button type="submit" className="support-chat__send" disabled={!draft.trim()} aria-label={t('support.send')}>
            <SendFill size={16} />
          </button>
        </form>
      )}
    </div>
  );
}

function Bubble({ mine, body, imageUrl, time, author, failed, onRetry }) {
  const { t } = useLocale();
  return (
    <div className={`support-msg${mine ? ' is-mine' : ''}${failed ? ' is-failed' : ''}`}>
      {author && <span className="support-msg__author">{author}</span>}
      {imageUrl && (
        <a className="support-msg__image" href={imageUrl} target="_blank" rel="noopener noreferrer">
          <img src={imageUrl} alt="" loading="lazy" />
        </a>
      )}
      {body && <div className="support-msg__bubble">{body}</div>}
      <span className="support-msg__meta">
        {failed ? (
          <button type="button" onClick={onRetry}>{t('support.retry')}</button>
        ) : (
          time
        )}
      </span>
    </div>
  );
}
